import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_PROMOTION_RULES } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { Badge, Button, Card, cx, ErrorText, Field, Input, PageHeader, Pagination, Select, Spinner, Switch } from "../components/ui.tsx";
import { keepPreviousData, useBrand, usePaging } from "../hooks.ts";

type Decision = "promoted" | "skipped" | "error";
const tabs: { id: Decision; label: string }[] = [
  { id: "promoted", label: "Promoted" },
  { id: "skipped", label: "Kept private" },
  { id: "error", label: "Errors" },
];

export function MemoryPage() {
  const brand = useBrand();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["memory"], queryFn: () => unwrap(api.admin.memory.$get()) });
  const [form, setForm] = useState<{ companyMemoryEnabled: boolean; promotionEnabled: boolean; promotionRules: string; memoryModel: string | null } | null>(null);
  useEffect(() => {
    if (q.data && !form) {
      const { companyMemoryEnabled, promotionEnabled, promotionRules, memoryModel } = q.data.settings;
      setForm({ companyMemoryEnabled, promotionEnabled, promotionRules, memoryModel });
    }
  }, [q.data, form]);
  const save = useMutation({
    mutationFn: (json: NonNullable<typeof form>) => unwrap(api.admin.memory.$put({ json })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["memory"] }),
  });
  const run = useMutation({
    mutationFn: () => unwrap(api.admin.memory.run.$post()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["memory"] }),
  });

  if (q.isPending || !form) return q.error ? <ErrorText error={q.error} /> : <div className="grid place-items-center py-16"><Spinner /></div>;
  const d = q.data!;
  const set = (p: Partial<typeof form>) => setForm({ ...form, ...p });

  return (
    <>
      <PageHeader
        title="Memory"
        description="Each person's assistant has its own private memory. Company memory is a separate, shared store every assistant can recall from — it only grows through the promotion rules below."
      />
      {!d.engineConfigured && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          The memory engine isn't connected to {brand.name} yet (missing system key), so promotion can't run.
        </div>
      )}
      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(form);
        }}
      >
        <Card className="p-6">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <h2 className="text-base font-semibold">Company memory</h2>
            <Badge>{d.stats.memories} memories</Badge>
          </div>
          <div className="grid gap-x-8 sm:grid-cols-2">
            <Switch
              label="Assistants recall company memory"
              hint="When on, every assistant also searches company memory. Results are labelled as company knowledge."
              checked={form.companyMemoryEnabled}
              onChange={(v) => set({ companyMemoryEnabled: v })}
            />
            <Switch
              label="Promote memories automatically"
              hint="Every 10 minutes, new memories from people whose group allows it are checked against the rules. Only memories formed after you switch this on are considered."
              checked={form.promotionEnabled}
              onChange={(v) => set({ promotionEnabled: v })}
            />
          </div>
          {d.settings.promotionEnabledAt && (
            <p className="mt-2 text-xs text-[var(--muted)]">Promoting memories formed since {new Date(d.settings.promotionEnabledAt).toLocaleString()}.</p>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="mb-1 text-base font-semibold">Promotion rules</h2>
          <p className="mb-4 text-sm text-[var(--muted)]">
            Plain-language instructions for the AI that decides what is shared. It rewrites each promoted memory to remove personal detail; when unsure, it keeps the memory private.
          </p>
          <textarea
            rows={14}
            value={form.promotionRules}
            onChange={(e) => set({ promotionRules: e.target.value })}
            className="w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
          />
          <div className="mt-2 flex justify-end">
            <button type="button" className="text-sm text-[var(--color-accent)] hover:underline" onClick={() => set({ promotionRules: DEFAULT_PROMOTION_RULES })}>
              Reset to default rules
            </button>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="mb-4 text-base font-semibold">Memory engine</h2>
          <Field label="Model" hint="Used to extract memories from conversations and to apply the promotion rules. Billed to the company, not to anyone's budget.">
            <Select value={form.memoryModel ?? ""} onChange={(e) => set({ memoryModel: e.target.value || null })}>
              <option value="">Automatic — the memory engine's own model if allowed, else {d.models[0]?.label ?? "the default"}</option>
              {d.models.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </Select>
          </Field>
        </Card>

        <ErrorText error={save.error} />
        <div className="flex items-center justify-end gap-3">
          {save.isSuccess && !save.isPending && <span className="text-sm text-[var(--muted)]">Saved</span>}
          <Button type="submit" variant="primary" disabled={save.isPending}>Save</Button>
        </div>
      </form>

      <Promotions running={run.isPending} onRun={() => run.mutate()} runResult={run.data} runError={run.error} canRun={d.settings.promotionEnabled && d.engineConfigured} />
    </>
  );
}

type Tab = "all" | Decision;
const browserTabs: { id: Tab; label: string }[] = [{ id: "all", label: "All company memory" }, ...tabs];
const TYPE_LABEL = { world: "Fact", experience: "Experience", observation: "Summary" } as const;

/** Where a memory came from, and who can recall it: the two things admins ask about a shared memory. */
function Provenance({ source, sharedWith }: { source: { kind: "person"; name: string; email: string; groupName: string | null } | { kind: "sync"; name: string } | { kind: "imported"; detail: string }; sharedWith: { everyone: boolean; groups: string[] } }) {
  return (
    <div className="mt-1.5 space-y-0.5 text-xs text-[var(--muted)]">
      <div>
        <span className="font-medium text-[var(--text)]">Source:</span>{" "}
        {source.kind === "person" ? (
          <>{source.name}{source.email && <> · {source.email}</>} · {source.groupName ?? "no group"}</>
        ) : source.kind === "sync" ? (
          <>Knowledge sync · {source.name}</>
        ) : (
          source.detail
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-medium text-[var(--text)]">Shared with:</span>
        {sharedWith.everyone ? (
          <Badge tone="good">Everyone</Badge>
        ) : sharedWith.groups.length ? (
          sharedWith.groups.map((g) => <Badge key={g} tone="accent">{g}</Badge>)
        ) : (
          <Badge>Nobody</Badge>
        )}
      </div>
    </div>
  );
}

function Promotions({ onRun, running, runResult, runError, canRun }: { onRun: () => void; running: boolean; runResult?: { users: number; promoted: number; skipped?: string }; runError: unknown; canRun: boolean }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("all");
  const [type, setType] = useState<"all" | "world" | "experience" | "observation">("all");
  const paging = usePaging([tab, type]);
  const { page, pageSize, q } = paging;

  const all = useQuery({
    queryKey: ["memory", "items", page, pageSize, q, type],
    queryFn: () => unwrap(api.admin.memory.items.$get({ query: { page: String(page), pageSize: String(pageSize), type, ...(q && { q }) } })),
    enabled: tab === "all",
    placeholderData: keepPreviousData,
  });
  const promos = useQuery({
    queryKey: ["memory", "promotions", tab, page, pageSize, q],
    queryFn: () => unwrap(api.admin.memory.promotions.$get({ query: { page: String(page), pageSize: String(pageSize), decision: tab as Decision, ...(q && { q }) } })),
    enabled: tab !== "all",
    placeholderData: keepPreviousData,
  });
  const cur = tab === "all" ? all : promos;
  const total = cur.data?.total;
  useEffect(() => void paging.clamp(total), [total]); // eslint-disable-line react-hooks/exhaustive-deps

  const revoke = useMutation({
    mutationFn: (id: number) => unwrap(api.admin.memory.promotions[":id"].$delete({ param: { id: String(id) } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["memory"] }),
  });
  return (
    <Card className="mt-8 overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--line)] px-4 py-3">
        {browserTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cx("rounded-md px-3 py-1.5 text-sm", tab === t.id ? "bg-[var(--panel-2)] font-medium" : "text-[var(--muted)] hover:text-[var(--text)]")}
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-3">
          {runResult && <span className="text-xs text-[var(--muted)]">{runResult.skipped ?? `Checked ${runResult.users} people · ${runResult.promoted} promoted`}</span>}
          <Button disabled={!canRun || running} onClick={onRun}>{running ? "Running…" : "Run now"}</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--line)] px-4 py-3">
        <Input value={paging.search} onChange={(e) => paging.setSearch(e.target.value)} placeholder={tab === "all" ? "Search company memory…" : "Search by text, person or email…"} className="max-w-sm" />
        {tab === "all" && (
          <Select value={type} onChange={(e) => setType(e.target.value as typeof type)} className="h-9 w-44">
            <option value="all">All kinds</option>
            <option value="world">Facts</option>
            <option value="experience">Experiences</option>
            <option value="observation">Summaries</option>
          </Select>
        )}
        {tab === "all" && <span className="text-xs text-[var(--muted)]">Summaries are combined from several memories.</span>}
      </div>
      <ErrorText error={runError ?? revoke.error} />
      {cur.isPending ? (
        <div className="grid place-items-center py-10"><Spinner /></div>
      ) : cur.error ? (
        <div className="p-4"><ErrorText error={cur.error} /></div>
      ) : (cur.data?.items.length ?? 0) === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-[var(--muted)]">{q ? "Nothing matches that search." : "Nothing here yet."}</p>
      ) : tab === "all" ? (
        <ul className={cx(cur.isPlaceholderData && "opacity-60")}>
          {all.data!.items.map((m) => (
            <li key={m.id} className="border-b border-[var(--line)] px-4 py-3 last:border-0">
              <div className="text-sm">{m.text}</div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--muted)]">
                <Badge>{TYPE_LABEL[m.type]}</Badge>
                {m.at && <span>{new Date(m.at).toLocaleString()}</span>}
              </div>
              <Provenance source={m.source} sharedWith={m.sharedWith} />
            </li>
          ))}
        </ul>
      ) : (
        <ul className={cx(cur.isPlaceholderData && "opacity-60")}>
          {promos.data!.items.map((p) => (
            <li key={p.id} className="border-b border-[var(--line)] px-4 py-3 last:border-0">
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className={cx("text-sm", p.revokedAt && "text-[var(--muted)] line-through")}>{p.sharedText ?? p.sourceText}</div>
                  {p.sharedText && p.sharedText !== p.sourceText && <div className="mt-1 text-xs text-[var(--muted)]">Original: {p.sourceText}</div>}
                  <div className="mt-1 text-xs text-[var(--muted)]">
                    {new Date(p.createdAt).toLocaleString()}
                    {p.reason && <> · {p.reason}</>}
                    {p.revokedAt && <> · removed by {p.revokedBy}</>}
                  </div>
                  {p.decision === "promoted" && !p.revokedAt ? (
                    <Provenance source={{ kind: "person", name: p.name, email: p.email, groupName: p.groupName }} sharedWith={{ everyone: true, groups: [] }} />
                  ) : (
                    <div className="mt-1.5 text-xs text-[var(--muted)]">
                      <span className="font-medium text-[var(--text)]">Source:</span> {p.name} · {p.email} · {p.groupName ?? "no group"}
                      {p.decision === "skipped" && <> · not shared</>}
                    </div>
                  )}
                </div>
                {p.decision === "promoted" && !p.revokedAt && (
                  <Button variant="danger" disabled={revoke.isPending} onClick={() => confirm("Remove this from company memory?") && revoke.mutate(p.id)}>
                    Remove
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <Pagination page={page} pageSize={pageSize} total={total ?? 0} onPage={paging.setPage} onPageSize={paging.setPageSize} busy={cur.isFetching} />
    </Card>
  );
}
